import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import { Download, Check, Trash2, Loader2, RefreshCw, CheckCheck } from 'lucide-react';

const PLATFORM_LABELS: Record<string, string> = {
  meta: 'Meta', shopify: 'Shopify', google_ads: 'Google Ads', ga4: 'GA4',
  pinterest: 'Pinterest', tiktok_shop: 'TikTok Shop', tiktok_business: 'TikTok Ads',
  amazon_seller: 'Amazon Seller', amazon_ads: 'Amazon Ads', klaviyo: 'Klaviyo',
};

interface LegacyStore {
  id: number;
  name: string;
  type: number;
  currency: string;
  timeZone: string;
  integrations: string[];
  imported: boolean;
}

interface MigrationResult {
  legacyStoreId: number;
  haloStoreId: number;
  storeName: string;
  migratedConnections: string[];
  errors: string[];
}

interface HaloStore {
  id: number;
  name: string;
  legacyId: number | null;
}

interface ShopifyRepairResult {
  total: number;
  repaired: number;
  skipped: number;
  errors: string[];
}

export function ImportLegacySection() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [results, setResults] = useState<readonly MigrationResult[]>([]);
  const [repairResult, setRepairResult] = useState<ShopifyRepairResult | null>(null);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'legacy-stores'],
    queryFn: async ({ signal }) => {
      const [legacyRes, haloRes] = await Promise.all([
        api.get<LegacyStore[]>('/api/v1/migration/legacy/stores', { signal }),
        api.get<HaloStore[]>('/api/v1/auth/stores', { signal }),
      ]);
      if (!legacyRes.success || !legacyRes.data) throw new Error(legacyRes.error ?? 'Failed to fetch legacy stores');
      return {
        stores: legacyRes.data,
        haloStores: haloRes.success && haloRes.data ? haloRes.data : [],
      };
    },
  });

  const stores = data?.stores ?? [];
  const haloStores = data?.haloStores ?? [];

  const legacyToHalo = useMemo(() => {
    const map = new Map<number, HaloStore>();
    for (const hs of haloStores) {
      if (hs.legacyId != null) map.set(hs.legacyId, hs);
    }
    return map;
  }, [haloStores]);

  const notImported = useMemo(
    () => stores.filter(s => !s.imported && !legacyToHalo.has(s.id)),
    [stores, legacyToHalo],
  );

  const importedStores = useMemo(
    () => stores.filter(s => s.imported || legacyToHalo.has(s.id)),
    [stores, legacyToHalo],
  );
  const importedCount = importedStores.length;

  function toggleStore(id: number) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll() {
    if (selected.size === notImported.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(notImported.map(s => s.id)));
    }
  }

  const migrateMutation = useMutation({
    mutationFn: async (storeIds: number[]) => {
      const res = await api.post<MigrationResult[]>('/api/v1/migration/legacy/migrate', { storeIds });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Migration failed');
      return res.data;
    },
    onSuccess: (data) => {
      setResults(data);
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ['admin', 'legacy-stores'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'stores'] });
    },
  });

  const unimportMutation = useMutation({
    mutationFn: async (haloStoreId: number) => {
      const res = await api.delete(`/api/v1/auth/stores/${haloStoreId}`);
      if (!res.success) throw new Error(res.error ?? 'Failed to remove store');
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'legacy-stores'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'stores'] });
      setResults([]);
    },
  });

  const repairShopifyMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<ShopifyRepairResult>('/api/v1/migration/legacy/repair-shopify', {});
      if (!res.success || !res.data) throw new Error(res.error ?? 'Shopify repair failed');
      return res.data;
    },
    onSuccess: (data) => {
      setRepairResult(data);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'legacy-stores'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'stores'] });
    },
  });

  function handleUnimport(legacyId: number) {
    const haloStore = legacyToHalo.get(legacyId);
    if (!haloStore) return;
    if (!window.confirm(`Delete "${haloStore.name}" and all its connections? This cannot be undone.`)) return;
    unimportMutation.mutate(haloStore.id);
  }

  const error =
    migrateMutation.error?.message
    ?? unimportMutation.error?.message
    ?? repairShopifyMutation.error?.message
    ?? null;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center">
            <Download className="w-3.5 h-3.5 text-orange-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Import Legacy Stores</h3>
            <p className="text-[10px] text-gray-500 dark:text-gray-400">Migrate stores from legacy Roasberry platform with OAuth integrations</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => repairShopifyMutation.mutate()}
            disabled={repairShopifyMutation.isPending}
            className="px-2.5 py-1.5 text-[10px] font-semibold rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-950/40 disabled:opacity-50 transition-colors flex items-center gap-1"
            title="Repair Shopify store URLs for already-migrated stores"
          >
            {repairShopifyMutation.isPending ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <RefreshCw className="w-3 h-3" />
            )}
            {repairShopifyMutation.isPending ? 'Repairing...' : 'Repair Shopify URLs'}
          </button>
          {stores.length > 0 && (
            <span className="text-[10px] text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
              {importedCount}/{stores.length} imported
            </span>
          )}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-2.5 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-lg text-xs text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {repairResult && (
        <div className="mb-3 p-2.5 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 rounded-lg text-xs text-amber-800 dark:text-amber-400">
          <span className="font-semibold">Shopify Repair:</span>{' '}
          scanned {repairResult.total}, repaired {repairResult.repaired}, skipped {repairResult.skipped}
          {repairResult.errors.length > 0 && (
            <span className="block mt-1 text-[10px]">First error: {repairResult.errors[0]}</span>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-400 dark:text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading legacy stores...
        </div>
      ) : stores.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">No legacy stores found.</p>
      ) : (
        <div>
          {/* Controls */}
          {notImported.length > 0 && (
            <div className="flex items-center justify-between mb-3">
              <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={notImported.length > 0 && selected.size === notImported.length}
                  onChange={selectAll}
                  className="rounded border-gray-300 dark:border-gray-700 text-emerald-600 focus:ring-emerald-500"
                />
                Select all available ({notImported.length})
                {selected.size > 0 && (
                  <span className="text-emerald-600 font-semibold">{selected.size} selected</span>
                )}
              </label>
              <button
                onClick={() => migrateMutation.mutate(Array.from(selected))}
                disabled={selected.size === 0 || migrateMutation.isPending}
                className="px-3 py-1.5 text-xs font-medium bg-gray-900 dark:bg-gray-950 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center gap-1.5"
              >
                {migrateMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                {migrateMutation.isPending ? 'Importing...' : `Import ${selected.size || ''}`}
              </button>
            </div>
          )}

          {/* Fix All Imported */}
          {importedCount > 0 && (
            <div className="flex items-center justify-between mb-3 px-3 py-2.5 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 rounded-lg">
              <div className="text-xs text-amber-800 dark:text-amber-400">
                <span className="font-semibold">{importedCount}</span> imported store{importedCount !== 1 ? 's' : ''} — re-run migration to repair Shopify URLs and connections
              </div>
              <button
                onClick={() => {
                  if (!window.confirm(`Re-migrate all ${importedCount} imported stores to repair connections?`)) return;
                  migrateMutation.mutate(importedStores.map(s => s.id));
                }}
                disabled={migrateMutation.isPending}
                className="px-3 py-1.5 text-xs font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors flex items-center gap-1.5 whitespace-nowrap"
              >
                {migrateMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                {migrateMutation.isPending ? 'Fixing...' : 'Fix All Imported'}
              </button>
            </div>
          )}

          {/* Table */}
          <div className="border border-gray-100 dark:border-gray-800 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-950 text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <th className="w-8 px-3 py-2 text-center" />
                  <th className="px-3 py-2 text-left">Store</th>
                  <th className="px-3 py-2 text-left">Integrations</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="w-10 px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {stores.map(store => {
                  const isImported = store.imported || legacyToHalo.has(store.id);
                  return (
                    <tr
                      key={store.id}
                      onClick={() => !isImported && toggleStore(store.id)}
                      className={`transition-colors ${
                        isImported
                          ? 'bg-emerald-50/30'
                          : selected.has(store.id)
                            ? 'bg-emerald-50/50 cursor-pointer'
                            : 'hover:bg-gray-50/50 cursor-pointer'
                      }`}
                    >
                      <td className="px-3 py-2.5 text-center">
                        {isImported ? (
                          <Check className="w-3.5 h-3.5 mx-auto text-emerald-500" />
                        ) : (
                          <input
                            type="checkbox"
                            checked={selected.has(store.id)}
                            onChange={() => toggleStore(store.id)}
                            onClick={e => e.stopPropagation()}
                            className="rounded border-gray-300 dark:border-gray-700 text-emerald-600 focus:ring-emerald-500"
                          />
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="font-medium text-gray-900 dark:text-gray-100">{store.name}</span>
                        <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-1.5">#{store.id} · {store.currency}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {store.integrations.slice(0, 5).map(p => (
                            <span key={p} className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                              {PLATFORM_LABELS[p] ?? p}
                            </span>
                          ))}
                          {store.integrations.length > 5 && (
                            <span className="text-[10px] text-gray-400 dark:text-gray-500">+{store.integrations.length - 5}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        {isImported ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-100 text-emerald-700">
                            <CheckCheck className="w-3 h-3" /> Imported
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                            Available
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                        {isImported && (
                          <button
                            onClick={() => handleUnimport(store.id)}
                            disabled={unimportMutation.isPending}
                            className="p-1 rounded text-red-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors disabled:opacity-50"
                            title="Remove imported store"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Results */}
          {results.length > 0 && (
            <div className="mt-3 space-y-1.5">
              <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold">Import Results</span>
              {results.map(r => (
                <div
                  key={r.legacyStoreId}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs ${
                    r.errors.length > 0
                      ? 'bg-amber-50 border border-amber-200 dark:border-amber-900/50 text-amber-800'
                      : 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                  }`}
                >
                  <div>
                    <span className="font-medium">{r.storeName}</span>
                    <span className="text-[10px] opacity-70 ml-1.5">#{r.legacyStoreId} → #{r.haloStoreId}</span>
                    {r.migratedConnections.length > 0 && (
                      <span className="text-[10px] opacity-70 ml-1.5">
                        ({r.migratedConnections.map(p => PLATFORM_LABELS[p] ?? p).join(', ')})
                      </span>
                    )}
                  </div>
                  {r.errors.length > 0 && (
                    <span className="text-[10px]">{r.errors[0]}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
